import React, { useState } from 'react';

import { Download } from 'lucide-react';

import {
	useImportPricingTemplatesMutation,
	type TemplateImportResult,
	type TemplateImportRow,
} from '../../api';
import {
	Alert,
	Badge,
	Button,
	FileUploadDropzone,
	Modal,
	Typography,
} from '../common';

export const TEMPLATE_COLUMNS = [
	'name',
	'partner_group',
	'rate_plan_type',
	'risk_tier',
	'discount_rate',
	'discount_floor',
	'discount_ceiling',
	'auth_fee',
	'auth_floor',
	'auth_ceiling',
	'monthly_fee',
	'batch_fee',
	'pci_fee',
	'assessments_mode',
	'amex_mode',
	'status',
];

const LAYOUT_EXAMPLE =
	'Retail IC+ standard,Prairie State Bank,IC_PLUS,LOW,0.28,0.22,0.35,0.10,0.08,0.15,25,0.10,4.95,standard,bundled:2.5,active';

const STATUS_BADGE = {
	ready: 'success',
	warning: 'warning',
	error: 'danger',
} as const;

type RowFilter = 'all' | TemplateImportRow['status'];

// A leading = + - @ makes a spreadsheet run the cell as a formula; prefix ' to keep it text.
const csvCell = (value: string | number | null) => {
	const text = String(value ?? '');
	const safe = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
	return `"${safe.replace(/"/g, '""')}"`;
};

export const buildErrorCsv = (rows: TemplateImportRow[]) =>
	[
		'row,name,messages',
		...rows
			.filter((row) => row.status === 'error')
			.map((row) =>
				[row.row, row.name, row.messages.join('; ')].map(csvCell).join(','),
			),
	].join('\n');

const download = (filename: string, text: string) => {
	const url = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
	const link = document.createElement('a');
	link.href = url;
	link.download = filename;
	link.click();
	URL.revokeObjectURL(url);
};

const errorMessage = (error: unknown) =>
	(error as { data?: { message?: string } })?.data?.message ||
	'The file could not be read.';

const checkLabel = (row: TemplateImportRow) => {
	if (row.status !== 'ready') return row.messages.join(' · ');
	if (row.action === 'unchanged') return 'Unchanged';
	return row.action === 'update' ? `Update → v${row.version}` : 'New';
};

interface ImportTemplatesModalProps {
	isOpen: boolean;
	onClose: () => void;
}

const ImportTemplatesModal: React.FC<ImportTemplatesModalProps> = ({
	isOpen,
	onClose,
}) => {
	const [file, setFile] = useState<File | null>(null);
	const [preview, setPreview] = useState<TemplateImportResult | null>(null);
	const [result, setResult] = useState<TemplateImportResult | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [filter, setFilter] = useState<RowFilter>('all');
	const [importTemplates, { isLoading }] = useImportPricingTemplatesMutation();

	const reset = () => {
		setFile(null);
		setPreview(null);
		setResult(null);
		setError(null);
		setFilter('all');
	};

	const close = () => {
		reset();
		onClose();
	};

	const run = async (selected: File, dryRun: boolean) => {
		setError(null);
		try {
			const response = await importTemplates({
				file: selected,
				dryRun,
			}).unwrap();
			if (dryRun) setPreview(response);
			else setResult(response);
		} catch (e) {
			setError(errorMessage(e));
		}
	};

	const selectFile = (selected: File) => {
		reset();
		setFile(selected);
		run(selected, true);
	};

	const shown = result ?? preview;
	const rows = (shown?.rows ?? []).filter(
		(row) => filter === 'all' || row.status === filter,
	);
	const importable = preview ? preview.ready + preview.warnings : 0;

	return (
		<Modal
			isOpen={isOpen}
			onClose={close}
			title="Import pricing templates"
			size="xl"
		>
			<div className="flex flex-col gap-4">
				<Typography variant="body2" color="muted">
					CSV, XLSX or JSON, one row per template, up to 10,000 rows. An
					existing name is updated as a new version.
				</Typography>

				{!preview && (
					<>
						<FileUploadDropzone
							title="Drop a file here or click to browse"
							description="CSV, XLSX or JSON"
							accept=".csv,.xlsx,.json"
							maxSize={20}
							onFileSelect={selectFile}
							selectedFile={file}
							onFileRemove={reset}
							disabled={isLoading}
						/>
						<div className="flex items-center justify-between">
							<Typography variant="caption" color="muted">
								Columns: {TEMPLATE_COLUMNS.join(', ')}
							</Typography>
							<Button
								variant="ghost"
								size="xs"
								icon={Download}
								onClick={() =>
									download(
										'pricing_templates_layout.csv',
										`${TEMPLATE_COLUMNS.join(',')}\n${LAYOUT_EXAMPLE}\n`,
									)
								}
							>
								Download the layout
							</Button>
						</div>
					</>
				)}

				{isLoading && (
					<Typography variant="body2">
						{preview ? 'Importing templates…' : 'Checking rows…'}
					</Typography>
				)}
				{error && <Alert variant="error">{error}</Alert>}

				{result && (
					<Alert variant="success">
						{result.created + result.updated} templates imported:{' '}
						{result.created} new, {result.updated} updated, {result.unchanged}{' '}
						unchanged, {result.warnings} flagged for review, {result.errors}{' '}
						skipped.
					</Alert>
				)}

				{shown && (
					<>
						<div className="flex gap-2" role="group" aria-label="Filter rows">
							{(
								[
									['all', 'All rows', shown.total],
									['ready', 'Ready', shown.ready],
									['warning', 'Warnings', shown.warnings],
									['error', 'Errors', shown.errors],
								] as const
							).map(([key, label, count]) => (
								<Button
									key={key}
									size="xs"
									variant={filter === key ? 'primary' : 'secondary'}
									onClick={() => setFilter(key)}
								>
									{label} · {count}
								</Button>
							))}
						</div>
						<div className="max-h-80 overflow-auto border border-gray-200 rounded-lg">
							<table className="w-full text-sm">
								<thead className="bg-gray-50 sticky top-0">
									<tr className="text-left text-xs text-gray-500 uppercase">
										<th className="px-3 py-2">Row</th>
										<th className="px-3 py-2">Name</th>
										<th className="px-3 py-2">Model</th>
										<th className="px-3 py-2">Tier</th>
										<th className="px-3 py-2">Check</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-gray-200">
									{rows.map((row) => (
										<tr key={row.row}>
											<td className="px-3 py-2 text-gray-500">{row.row}</td>
											<td className="px-3 py-2">{row.name || '—'}</td>
											<td className="px-3 py-2">{row.rate_plan_type ?? '—'}</td>
											<td className="px-3 py-2">{row.risk_tier ?? '—'}</td>
											<td className="px-3 py-2">
												<Badge variant={STATUS_BADGE[row.status]}>
													{checkLabel(row)}
												</Badge>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</>
				)}

				<div className="flex justify-between gap-2">
					<div>
						{shown && shown.errors > 0 && (
							<Button
								variant="secondary"
								icon={Download}
								onClick={() =>
									download(
										'pricing_template_errors.csv',
										buildErrorCsv(shown.rows),
									)
								}
							>
								Download error rows
							</Button>
						)}
					</div>
					<div className="flex gap-2">
						{preview && !result && (
							<Button variant="secondary" onClick={reset} disabled={isLoading}>
								Choose another file
							</Button>
						)}
						{preview && !result ? (
							<Button
								onClick={() => file && run(file, false)}
								disabled={isLoading || importable === 0}
							>
								Import {importable} templates
							</Button>
						) : (
							<Button variant="secondary" onClick={close}>
								{result ? 'Done' : 'Cancel'}
							</Button>
						)}
					</div>
				</div>
			</div>
		</Modal>
	);
};

export default ImportTemplatesModal;
